import {avatarAnatomy} from '@chakra-ui/anatomy';
import type {SystemStyleObject, useStyleConfig} from '@chakra-ui/react';
import {createMultiStyleConfigHelpers, defineCssVars, useMultiStyleConfig} from '@chakra-ui/react';

import {getColorScheme} from '../../theme-config';

export enum AvatarVariant {
  Solid = `solid`,
  Outline = `outline`,
  SolidNew = `solid_new`,
}

type Part = (typeof avatarAnatomy.keys)[number];
const {defineMultiStyleConfig} = createMultiStyleConfigHelpers(avatarAnatomy.keys);

const vars = defineCssVars(`avatar`, [`size`]);
const {bg: $bg, borderColor: $borderColor, color: $color} = defineCssVars(`avatar`, [`bg`, `borderColor`, `color`]);

export const avatarTheme = defineMultiStyleConfig({
  baseStyle: ({colorScheme}) => {
    const {value} = getColorScheme(colorScheme);
    return {
      container: {
        [`&:not([data-loaded])`]: {
          [$bg.variable]: `colors.${value.bg}`,
          [$borderColor.variable]: `colors.${value.borderColor}`,
          [$color.variable]: `colors.${value.color}`,
          color: $color.reference,
          borderColor: $borderColor.reference,
        },
      },
    };
  },
  variants: {
    [AvatarVariant.Solid]: ({colorScheme}) => {
      const {value} = getColorScheme(colorScheme);
      return {
        container: {
          [`&:not([data-loaded])`]: {
            borderColor: `transparent`,
            [$bg.variable]: `colors.${value.bg}`,
            [$color.variable]: `colors.${value.color}`,
            color: $color.reference,
          },
        },
      };
    },
    [AvatarVariant.SolidNew]: ({colorScheme}) => {
      const {value} = getColorScheme(colorScheme);
      return {
        container: {
          [`&:not([data-loaded])`]: {
            [$bg.variable]: `colors.${value.bg}`,
            [$color.variable]: `colors.${value.color}`,
            color: `black`,
          },
          backgroundColor: `white`,
          borderColor: `brand.neutral.200 !important`,
          borderWidth: `1px`,
        },
      };
    },
    [AvatarVariant.Outline]: ({colorScheme}) => {
      const {value} = getColorScheme(colorScheme);
      return {
        container: {
          [`&:not([data-loaded])`]: {
            [$bg.variable]: `transparent`,
            [$borderColor.variable]: `colors.${value.borderColor}`,
            [$color.variable]: `colors.${value.color}`,
            color: $color.reference,
          },
          borderStyle: `dashed`,
          borderWidth: `1px`,
        },
      };
    },
  },
  sizes: {
    sm: {
      [vars.size.variable]: `32px`,
    },
  },
  defaultProps: {
    variant: AvatarVariant.Solid,
    colorScheme: `obsidian`,
  },
});

export const useAvatarStyleConfig = (props: Parameters<typeof useStyleConfig>[1]): Record<Part, SystemStyleObject> => {
  return useMultiStyleConfig(`Avatar`, props);
};

export default avatarTheme;
